import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { useBillingStore, PaymentMethod } from '../../store/billingStore';
import { useGamingStore } from '../../store/gamingStore';
import { useToastStore } from '../../store/toastStore';
import {
  X, CreditCard, Wallet, Loader2, CheckCircle2,
  ChevronDown, Lock, ShieldCheck, ArrowRight,
} from 'lucide-react';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY || '');

/* ── Brand label helpers ── */
const brandLabel: Record<string, string> = {
  visa: 'Visa', mastercard: 'Mastercard', amex: 'Amex', discover: 'Discover',
};

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  PaymentDrawer — slides from right                                        */
/* ═══════════════════════════════════════════════════════════════════════════ */

const PaymentDrawer: React.FC = () => {
  const { drawerOpen, drawerItem, closePaymentDrawer, methods, wallet, fetchMethods, fetchWallet } = useBillingStore();
  const [isClosing, setIsClosing] = useState(false);

  // Load data when drawer opens
  useEffect(() => {
    if (drawerOpen) {
      fetchMethods();
      fetchWallet();
    }
  }, [drawerOpen]);

  // Close with animation — refresh data as needed
  const handleClose = useCallback(() => {
    if (drawerItem?.type === 'wallet_topup') fetchWallet();
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      closePaymentDrawer();
    }, 250);
  }, [closePaymentDrawer, drawerItem, fetchWallet]);

  // ESC to close
  useEffect(() => {
    if (!drawerOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [drawerOpen, handleClose]);

  if (!drawerOpen || !drawerItem) return null;

  return ReactDOM.createPortal(
    <div
      className={`fixed inset-0 z-[250] flex justify-end ${isClosing ? 'animate-drawer-overlay-out' : 'animate-drawer-overlay-in'}`}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div
        className={`w-full max-w-md h-full bg-bg-primary border-l border-border-color shadow-2xl shadow-black/60 flex flex-col ${
          isClosing ? 'animate-drawer-slide-out' : 'animate-drawer-slide-in'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-color">
          <h2 className="text-base font-bold text-text-primary">Paiement</h2>
          <button
            onClick={handleClose}
            className="w-7 h-7 rounded-lg bg-bg-tertiary/80 border border-border-color flex items-center justify-center text-text-muted hover:text-text-primary transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 gaming-scrollbar">
          <DrawerContent
            item={drawerItem}
            methods={methods}
            wallet={wallet}
            onSuccess={handleClose}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
};

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  DrawerContent — handles payment logic                                    */
/* ═══════════════════════════════════════════════════════════════════════════ */

interface DrawerContentProps {
  item: NonNullable<ReturnType<typeof useBillingStore.getState>['drawerItem']>;
  methods: PaymentMethod[];
  wallet: { balance: number; currency: string } | null;
  onSuccess: () => void;
}

const DrawerContent: React.FC<DrawerContentProps> = ({ item, methods, wallet, onSuccess }) => {
  const { addToast } = useToastStore();
  const { initiateGamePurchase, createWalletTopup, createSubscription, fetchWallet, fetchSubscriptions } = useBillingStore();
  const { fetchLibrary } = useGamingStore();

  const [payWith, setPayWith] = useState<'card' | 'wallet' | 'new_card'>('card');
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [showCardPicker, setShowCardPicker] = useState(false);
  const [topupAmount, setTopupAmount] = useState(item.type === 'wallet_topup' ? item.amount : 10);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [saveCard, setSaveCard] = useState(true);

  const walletBalance = wallet?.balance ?? 0;
  const isSubscription = item.type === 'erozion_plus' || item.type === 'hosting_subscription';
  const canPayWithWallet = !isSubscription && item.type !== 'wallet_topup' && walletBalance >= item.amount;
  const defaultCard = methods.find((m) => m.isDefault) || methods[0];

  // Select default payment method
  useEffect(() => {
    if (methods.length > 0 && !selectedCardId) {
      setSelectedCardId(defaultCard?.id || null);
    }
    if (canPayWithWallet && methods.length === 0) {
      setPayWith('wallet');
    }
  }, [methods, wallet]);

  const selectedCard = methods.find((m) => m.id === selectedCardId);
  const effectiveAmount = item.type === 'wallet_topup' ? topupAmount : item.amount;

  /* ── Pay with saved card or new card (both go through Stripe) ── */
  const handlePayWithCard = async () => {
    setIsProcessing(true);
    setPaymentError(null);

    // When new_card is selected, don't send a saved card ID
    const cardId = payWith === 'new_card' ? undefined : (selectedCardId || undefined);

    try {
      if (item.type === 'game_purchase' && item.itemId) {
        const result = await initiateGamePurchase(item.itemId, cardId, false, payWith === 'new_card' ? saveCard : undefined);
        if (result?.completed) {
          setSuccess(true);
          fetchLibrary();
          addToast({ type: 'system', title: 'Achat réussi', body: `${item.itemName} ajouté à votre bibliothèque`, duration: 4000 });
          setTimeout(onSuccess, 1500);
        } else if (result?.clientSecret) {
          setClientSecret(result.clientSecret);
        } else if (result?.failed || !result) {
          setPaymentError(result?.message || 'Votre carte a été refusée.');
        }
      } else if (item.type === 'wallet_topup') {
        const result = await createWalletTopup(topupAmount, item.currency, cardId, payWith === 'new_card' ? saveCard : undefined);
        if (result?.completed) {
          setSuccess(true);
          fetchWallet();
          addToast({ type: 'system', title: 'Recharge réussie', body: `${topupAmount.toFixed(2)} ${item.currency.toUpperCase()} ajoutés`, duration: 4000 });
          setTimeout(onSuccess, 1500);
        } else if (result?.clientSecret) {
          setClientSecret(result.clientSecret);
        } else if (!result) {
          setPaymentError('Votre carte a été refusée.');
        }
      } else if (isSubscription && item.itemId) {
        // Subscription: create via billing API, get clientSecret back
        const result = await createSubscription(item.itemId, cardId);
        if (!result || result.error) {
          setPaymentError(result?.error || 'Impossible de créer l\'abonnement.');
        } else if (result.status === 'active' || result.status === 'trialing') {
          // Trial or already-active (shouldn't happen often but handle it)
          setSuccess(true);
          fetchSubscriptions();
          addToast({
            type: 'system',
            title: 'Abonnement activé!',
            body: result.status === 'trialing'
              ? `Votre essai gratuit de ${item.itemName} a commencé.`
              : `Vous êtes abonné à ${item.itemName}.`,
            duration: 4000,
          });
          setTimeout(onSuccess, 1500);
        } else if (result.clientSecret) {
          if (cardId) {
            // Saved card — confirm directly without showing a form
            const stripe = await stripePromise;
            if (!stripe) { setPaymentError('Stripe non initialisé.'); return; }
            const { error } = await stripe.confirmCardPayment(result.clientSecret, {
              payment_method: cardId,
            });
            if (error) {
              setPaymentError(error.message || 'Le paiement a été refusé.');
            } else {
              setSuccess(true);
              fetchSubscriptions();
              addToast({ type: 'system', title: 'Abonnement activé!', body: `Vous êtes abonné à ${item.itemName}.`, duration: 4000 });
              setTimeout(onSuccess, 1500);
            }
          } else {
            // New card — show PaymentElement form
            setClientSecret(result.clientSecret);
          }
        } else {
          setPaymentError('L\'abonnement n\'a pas pu être activé.');
        }
      }
    } catch {
      setPaymentError('Une erreur est survenue. Réessayez.');
    } finally {
      setIsProcessing(false);
    }
  };

  /* ── Pay with wallet ── */
  const handlePayWithWallet = async () => {
    if (!canPayWithWallet || !item.itemId) return;
    setIsProcessing(true);
    setPaymentError(null);

    try {
      const result = await initiateGamePurchase(item.itemId, undefined, true);
      if (result?.completed) {
        setSuccess(true);
        fetchLibrary();
        addToast({ type: 'system', title: 'Achat réussi', body: `${item.itemName} ajouté à votre bibliothèque`, duration: 4000 });
        fetchWallet();
        setTimeout(onSuccess, 1500);
      } else if (result?.clientSecret) {
        // Partial wallet + card needed
        setClientSecret(result.clientSecret);
      }
    } catch {
      addToast({ type: 'system', title: 'Erreur', body: 'Le paiement a échoué', duration: 4000 });
    } finally {
      setIsProcessing(false);
    }
  };

  /* ── Success state ── */
  if (success) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mb-4">
          <CheckCircle2 className="h-8 w-8 text-success" />
        </div>
        <h3 className="text-lg font-bold text-text-primary">Paiement réussi!</h3>
        <p className="text-sm text-text-muted mt-1">{item.itemName}</p>
      </div>
    );
  }

  /* ── If we have a clientSecret, show Stripe PaymentElement ── */
  if (clientSecret) {
    return (
      <Elements
        stripe={stripePromise}
        options={{
          clientSecret,
          appearance: { theme: 'night', variables: { colorPrimary: '#00ccff' } },
        }}
      >
        <StripeConfirmForm
          clientSecret={clientSecret}
          itemName={item.itemName}
          amount={effectiveAmount}
          currency={item.currency}
          onSuccess={() => {
            setSuccess(true);
            addToast({ type: 'system', title: 'Paiement réussi', body: item.itemName, duration: 4000 });
            if (item.type === 'wallet_topup') fetchWallet();
            if (item.type === 'game_purchase') fetchLibrary();
            if (isSubscription) fetchSubscriptions();
            setTimeout(onSuccess, 1500);
          }}
          onError={(msg) => {
            setClientSecret(null);
            addToast({ type: 'system', title: 'Erreur', body: msg, duration: 5000 });
          }}
        />
      </Elements>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Item summary ── */}
      <div className="rounded-xl bg-bg-secondary/60 border border-border-color overflow-hidden">
        {item.imageUrl && (
          <img src={item.imageUrl} alt={item.itemName} className="w-full h-32 object-cover" />
        )}
        <div className="px-4 py-3">
          <p className="text-sm font-bold text-text-primary">{item.itemName}</p>
          {item.description && (
            <p className="text-xs text-text-muted mt-0.5">{item.description}</p>
          )}
          <p className="text-lg font-bold text-text-primary mt-2">
            {effectiveAmount.toFixed(2)}&nbsp;
            <span className="text-sm font-normal text-text-muted">{item.currency.toUpperCase()}</span>
          </p>
        </div>
      </div>

      {/* ── Topup amount selector (wallet_topup only) ── */}
      {item.type === 'wallet_topup' && (
        <div>
          <label className="block text-xs font-semibold text-text-secondary mb-2">Montant</label>
          <div className="grid grid-cols-4 gap-2">
            {[5, 10, 25, 50].map((v) => (
              <button
                key={v}
                onClick={() => setTopupAmount(v)}
                className={`py-2 rounded-lg text-sm font-semibold border transition-colors ${
                  topupAmount === v
                    ? 'bg-accent-color/10 border-accent-color text-accent-color'
                    : 'bg-bg-secondary border-border-color text-text-secondary hover:border-accent-color/30'
                }`}
              >
                {v}$
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Payment method selection ── */}
      <div>
        <label className="block text-xs font-semibold text-text-secondary mb-2">Payer avec</label>
        <div className="space-y-2">
          {/* Saved cards */}
          {methods.length > 0 && (
            <div>
              <button
                onClick={() => { setPayWith('card'); setShowCardPicker((v) => !v); }}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-colors ${
                  payWith === 'card'
                    ? 'bg-accent-color/5 border-accent-color/40'
                    : 'bg-bg-secondary/60 border-border-color hover:border-accent-color/20'
                }`}
              >
                <div className="flex items-center gap-3">
                  <CreditCard className="h-4 w-4 text-text-muted" />
                  <span className="text-sm font-medium text-text-primary">
                    {selectedCard
                      ? `${brandLabel[selectedCard.brand] || selectedCard.brand} ••••${selectedCard.last4}`
                      : 'Carte enregistrée'}
                  </span>
                </div>
                <ChevronDown className={`h-4 w-4 text-text-muted transition-transform ${showCardPicker ? 'rotate-180' : ''}`} />
              </button>
              {showCardPicker && payWith === 'card' && (
                <div className="mt-1 rounded-xl bg-bg-secondary border border-border-color overflow-hidden">
                  {methods.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => { setSelectedCardId(m.id); setShowCardPicker(false); }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                        selectedCardId === m.id
                          ? 'bg-accent-color/10 text-accent-color'
                          : 'text-text-secondary hover:bg-highlight/50'
                      }`}
                    >
                      <CreditCard className="h-4 w-4" />
                      {brandLabel[m.brand] || m.brand} ••••{m.last4}
                      {m.isDefault && <span className="text-[10px] opacity-60 ml-auto">par défaut</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Wallet (not for topups or subscriptions) */}
          {!isSubscription && item.type !== 'wallet_topup' && (
            <button
              onClick={() => setPayWith('wallet')}
              disabled={!canPayWithWallet}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-colors ${
                payWith === 'wallet'
                  ? 'bg-accent-color/5 border-accent-color/40'
                  : 'bg-bg-secondary/60 border-border-color hover:border-accent-color/20'
              } disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              <div className="flex items-center gap-3">
                <Wallet className="h-4 w-4 text-text-muted" />
                <span className="text-sm font-medium text-text-primary">Portefeuille</span>
              </div>
              <span className="text-sm text-text-muted">{walletBalance.toFixed(2)}$</span>
            </button>
          )}

          {/* New card — always available */}
          <button
            onClick={() => setPayWith('new_card')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-colors ${
              payWith === 'new_card'
                ? 'bg-accent-color/5 border-accent-color/40'
                : 'bg-bg-secondary/60 border-border-color hover:border-accent-color/20'
            }`}
          >
            <CreditCard className="h-4 w-4 text-text-muted" />
            <span className="text-sm font-medium text-text-primary">Nouvelle carte</span>
          </button>

          {/* Save card checkbox (only for new card) */}
          {payWith === 'new_card' && (
            <label className="flex items-start gap-3 px-4 py-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={saveCard}
                onChange={(e) => setSaveCard(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-border-color bg-bg-secondary text-accent-color focus:ring-accent-color/30 accent-[rgb(var(--accent-color))]"
              />
              <div>
                <span className="text-sm font-medium text-text-primary">Enregistrer la carte</span>
                <p className="text-xs text-text-muted mt-0.5">
                  Sauvegardez cette carte pour vos prochains achats. Sinon, elle ne sera utilisée que pour ce paiement.
                </p>
              </div>
            </label>
          )}
        </div>
      </div>

      {/* ── Inline payment error ── */}
      {paymentError && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-error/10 border border-error/30">
          <span className="text-error text-sm font-medium">{paymentError}</span>
        </div>
      )}

      {/* ── Security notice ── */}
      <div className="flex items-center gap-2 text-[11px] text-text-muted">
        <Lock className="h-3 w-3 flex-shrink-0" />
        Paiement sécurisé par Stripe. Vos données sont protégées.
      </div>

      {/* ── Confirm button ── */}
      <button
        onClick={
          payWith === 'wallet' ? handlePayWithWallet : handlePayWithCard
        }
        disabled={isProcessing || (payWith === 'wallet' && !canPayWithWallet)}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-accent-color text-black font-bold text-sm hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isProcessing ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <ShieldCheck className="h-4 w-4" />
        )}
        {isProcessing
          ? 'Traitement…'
          : `Payer ${effectiveAmount.toFixed(2)} ${item.currency.toUpperCase()}`}
      </button>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  StripeConfirmForm — confirms a PaymentIntent via PaymentElement          */
/* ═══════════════════════════════════════════════════════════════════════════ */

interface ConfirmFormProps {
  clientSecret: string;
  itemName: string;
  amount: number;
  currency: string;
  onSuccess: () => void;
  onError: (msg: string) => void;
}

const StripeConfirmForm: React.FC<ConfirmFormProps> = ({ itemName, amount, currency, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ready, setReady] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setIsSubmitting(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });

    if (error) {
      onError(error.message || 'Le paiement a échoué');
      setIsSubmitting(false);
    } else {
      onSuccess();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="rounded-xl bg-bg-secondary/60 border border-border-color px-4 py-3">
        <p className="text-sm font-bold text-text-primary">{itemName}</p>
        <p className="text-lg font-bold text-text-primary mt-1">
          {amount.toFixed(2)} <span className="text-sm font-normal text-text-muted">{currency.toUpperCase()}</span>
        </p>
      </div>

      <div>
        <label className="block text-xs font-semibold text-text-secondary mb-2">
          Détails du paiement
        </label>
        <PaymentElement
          onReady={() => setReady(true)}
          options={{ layout: 'tabs' }}
        />
      </div>

      <button
        type="submit"
        disabled={!stripe || !ready || isSubmitting}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-accent-color text-black font-bold text-sm hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <ArrowRight className="h-4 w-4" />
        )}
        {isSubmitting ? 'Traitement…' : `Confirmer le paiement`}
      </button>

      <div className="flex items-center gap-2 text-[11px] text-text-muted">
        <Lock className="h-3 w-3 flex-shrink-0" />
        Sécurisé par Stripe
      </div>
    </form>
  );
};

export default PaymentDrawer;
